/**
 * Correlation-id primitives for API calls.
 *
 * A fresh id is attached to every outgoing request as `X-Correlation-ID`. The
 * backend (asgi-correlation-id) accepts an incoming id, binds it for the whole
 * request, echoes it on the response, and stamps it on every log line — so a
 * single UI action can be traced end-to-end from the browser call into the
 * server logs.
 *
 * The id must satisfy the backend's sanitizer: charset `[A-Za-z0-9._-]`,
 * length <= 64. `crypto.randomUUID()` (36-char hyphenated hex) fits.
 */

export const CORRELATION_ID_HEADER = "X-Correlation-ID";

/** Generate a per-request correlation id (UUIDv4 when available). */
export function generateCorrelationId(): string {
  const cryptoObj = globalThis.crypto as Crypto | undefined;
  if (typeof cryptoObj?.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID — still within the
  // backend's allowed charset and length.
  const rand = Math.random().toString(36).slice(2, 12);
  return `${Date.now().toString(36)}-${rand}`;
}

/**
 * Return `headers` with a correlation id attached for tracing — the single place
 * that owns the "attach a fresh id, but never overwrite one the caller already
 * set (any case)" rule. Shared by the versioned api-client and the unversioned
 * auth-client so both trace calls identically.
 */
export function withCorrelationId(
  headers: Record<string, string>,
): Record<string, string> {
  const hasCorrelationId = Object.keys(headers).some(
    (key) => key.toLowerCase() === CORRELATION_ID_HEADER.toLowerCase(),
  );
  if (hasCorrelationId) {
    return headers;
  }
  return { ...headers, [CORRELATION_ID_HEADER]: generateCorrelationId() };
}
